import { useState, useEffect } from 'react'
import api from '../../services/api'
import Button from '../../components/common/Button'
import Modal from '../../components/common/Modal'
import ErrorMessage from '../../components/common/ErrorMessage'
import Loading from '../../components/common/Loading'
import TeamTable from './components/TeamTable'
import TeamForm from './components/TeamForm'
import DeleteConfirm from './components/DeleteConfirm'
import './Team.css'

export interface TeamMember {
  id: number
  name: string
  role: string
  bio: string
  media_id?: number
  photo?: {
    id: number
    url: string
    alt: string | null
    title: string | null
  } | null
  position: number
  featured: boolean
  is_active: boolean
}

export interface TeamFormData {
  name: string
  role: string
  bio: string
  media_id?: number
  position: number
  featured: boolean
  is_active: boolean
}

function Team() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [deletingMember, setDeletingMember] = useState<TeamMember | null>(null)

  useEffect(() => {
    loadMembers()
  }, [])

  const loadMembers = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api.get<TeamMember[]>('/team?active=all')
      setMembers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingMember(null)
    setIsFormModalOpen(true)
  }

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member)
    setIsFormModalOpen(true)
  }

  const handleDelete = (member: TeamMember) => {
    setDeletingMember(member)
    setIsDeleteModalOpen(true)
  }

  const handleFormSubmit = async (formData: TeamFormData) => {
    try {
      if (editingMember) {
        await api.put(`/team/${editingMember.id}`, formData)
      } else {
        await api.post('/team', formData)
      }
      setIsFormModalOpen(false)
      loadMembers()
    } catch (err) {
      throw err
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deletingMember) return

    try {
      await api.delete(`/team/${deletingMember.id}`)
      setIsDeleteModalOpen(false)
      setDeletingMember(null)
      loadMembers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const handleToggleFeatured = async (member: TeamMember) => {
    try {
      await api.put(`/team/${member.id}`, {
        ...member,
        featured: !member.featured,
      })
      loadMembers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour')
    }
  }

  const handleToggleActive = async (member: TeamMember) => {
    try {
      await api.put(`/team/${member.id}`, {
        ...member,
        is_active: !member.is_active,
      })
      loadMembers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour')
    }
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className="team-management">
      <div className="team-header">
        <h1>Gestion de l'équipe</h1>
        <Button onClick={handleAdd}>Ajouter un membre</Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <TeamTable
        members={members}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleFeatured={handleToggleFeatured}
        onToggleActive={handleToggleActive}
      />

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title={editingMember ? 'Modifier le membre' : 'Ajouter un membre'}
      >
        <TeamForm
          initialData={editingMember || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormModalOpen(false)}
        />
      </Modal>

      <DeleteConfirm
        isOpen={isDeleteModalOpen}
        memberName={deletingMember?.name || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false)
          setDeletingMember(null)
        }}
      />
    </div>
  )
}

export default Team
